import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import Breadcrumb from '../components/layout/Breadcrumb';
import { PAGE_SIDEBAR_LAYOUT, PageSidebar } from '../components/layout/OnThisPage';
import InThisNav, { InThisNavItem } from '../components/programs/InThisNav';
import ResourceCard from '../components/resources/ResourceCard';
import ResourceCardSkeleton from '../components/resources/ResourceCardSkeleton';
import ErrorState from '../components/ui/ErrorState';
import LoadingSkeleton from '../components/ui/LoadingSkeleton';
import HubDetail from './HubDetail';
import { usePageTitle } from '../hooks/usePageTitle';
import { useScrollSpy } from '../hooks/useScrollSpy';
import { programAPI } from '../services/programApi';
import { wordpressAPI } from '../services/wordpressApi';
import { Program, ProgramSection } from '../types/program';
import { WPPost } from '../types/wordpress';
import { sectionAnchors } from '../utils/headingToc';
import { hashScrollBehavior, pinScrollAnchor, withSmoothScroll } from '../utils/hubChapter';
import { placePath } from '../utils/placePaths';
import { stickySectionHeadingClass } from '../utils/stickySectionHeading';

function descendants(sectionId: string, sections: ProgramSection[]): ProgramSection[] {
  const children = sections
    .filter((section) => section.parentId === sectionId)
    .sort((a, b) => a.order - b.order);
  return children.flatMap((child) => [child, ...descendants(child.id, sections)]);
}

function SectionPage({ program, section }: { program: Program; section: ProgramSection }) {
  const [posts, setPosts] = useState<Map<number, WPPost>>(new Map());
  const [postsReady, setPostsReady] = useState(false);
  usePageTitle(section.title);
  const tree = useMemo(
    () => [section, ...descendants(section.id, program.sections)],
    [program.sections, section]
  );
  const highlights = useMemo(() => {
    const ids = new Set(tree.map((item) => item.id));
    return program.highlights
      .filter((highlight) => highlight.sectionId && ids.has(highlight.sectionId))
      .sort((a, b) => a.order - b.order);
  }, [program.highlights, tree]);

  useEffect(() => {
    let cancelled = false;
    setPostsReady(false);
    setPosts(new Map());
    const load = async () => {
      const map = new Map<number, WPPost>();
      for (let index = 0; index < highlights.length; index += 8) {
        const slice = highlights.slice(index, index + 8);
        const rows = await Promise.all(
          slice.map(async (highlight) => {
            try {
              return [highlight.wpPostId, await wordpressAPI.fetchPost(highlight.wpPostId)] as const;
            } catch {
              return null;
            }
          })
        );
        if (cancelled) return;
        for (const row of rows) {
          if (row) map.set(row[0], row[1]);
        }
        setPosts(new Map(map));
      }
      if (!cancelled) setPostsReady(true);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [highlights]);

  const location = useLocation();
  const navigate = useNavigate();
  const anchors = useMemo(
    () => sectionAnchors(tree.map((item) => ({ id: item.id, title: item.title }))),
    [tree]
  );
  const navItems = useMemo(() => {
    const itemFor = (node: ProgramSection): InThisNavItem => {
      const id = anchors.get(node.id) ?? node.id;
      const children = tree.filter((item) => item.parentId === node.id).map(itemFor);
      return {
        id,
        label: node.title,
        children: children.length > 0 ? children : undefined,
      };
    };
    return tree.filter((item) => item.parentId === section.id).map(itemFor);
  }, [anchors, section.id, tree]);
  const navIds = useMemo(() => {
    const ids: string[] = [];
    const walk = (items: InThisNavItem[]) => {
      for (const item of items) {
        ids.push(item.id);
        if (item.children) walk(item.children);
      }
    };
    walk(navItems);
    return ids;
  }, [navItems]);
  const activeSectionId = useScrollSpy(navIds);

  useEffect(() => {
    const id = location.hash.replace(/^#/, '');
    if (!id) return;
    return pinScrollAnchor(id, hashScrollBehavior(location.state));
  }, [location.hash]);

  const groups = tree.map((item) => ({
    section: item,
    highlights: highlights.filter((highlight) => highlight.sectionId === item.id),
  }));
  const sidebarItems = navItems.map((item) => {
    const mark = (node: InThisNavItem): InThisNavItem => ({
      ...node,
      current: node.id === activeSectionId,
      children: node.children?.map(mark),
    });
    return mark(item);
  });

  return (
    <div className="bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumb
          items={[
            { label: 'Home', to: '/' },
            { label: 'Resources', to: '/resources' },
            { label: program.title, to: placePath(program) },
            { label: section.title },
          ]}
        />
        <h1 className="mt-6 text-3xl font-bold text-gray-900">{section.title}</h1>
        <p className="mt-3">
          <Link to={placePath(program)} className="text-sm font-medium text-primary hover:text-primary-dark">
            Back to {program.title}
          </Link>
        </p>
        <div className={sidebarItems.length > 0 ? `mt-8 ${PAGE_SIDEBAR_LAYOUT}` : 'mt-8'}>
          {sidebarItems.length > 0 ? (
            <PageSidebar>
              <InThisNav
                label="On this page"
                items={sidebarItems}
                onSelect={(id) => {
                  navigate({ hash: id }, { preventScrollReset: true, state: withSmoothScroll(location.state) });
                }}
              />
            </PageSidebar>
          ) : null}
          <div className="min-w-0 space-y-10">
            {groups.map((group) => (
              <section key={group.section.id} id={anchors.get(group.section.id)} className="scroll-mt-24">
                {group.section.id !== section.id && (
                  <h2 className={`text-2xl font-semibold text-gray-900 ${stickySectionHeadingClass(0)}`}>
                    {group.section.title}
                  </h2>
                )}
                <div className={`space-y-4 ${group.section.id !== section.id ? 'mt-3' : ''}`}>
                  {group.highlights.map((highlight) => {
                    const post = posts.get(highlight.wpPostId);
                    if (post) return <ResourceCard key={highlight.id} post={post} view="list" />;
                    if (postsReady) return null;
                    return <ResourceCardSkeleton key={highlight.id} />;
                  })}
                </div>
              </section>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function PlaceChild() {
  const { subjectSlug, childSlug } = useParams<{ subjectSlug: string; childSlug: string }>();
  const [program, setProgram] = useState<Program | null>(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    if (!subjectSlug || !childSlug) return;
    let cancelled = false;
    setMissing(false);
    setProgram(null);
    programAPI
      .fetchProgramBySlug(subjectSlug)
      .then((row) => {
        if (cancelled) return;
        if (!row || row.kind === 'REFERENCE') {
          setMissing(true);
          return;
        }
        const hub = row.hubs.some((item) => item.slug === childSlug);
        const section = row.sections.some((item) => item.slug === childSlug);
        if (!hub && !section) setMissing(true);
        else setProgram(row);
      })
      .catch(() => {
        if (!cancelled) setMissing(true);
      });
    return () => {
      cancelled = true;
    };
  }, [subjectSlug, childSlug]);

  if (missing) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <ErrorState error={new Error('That page was not found')} title="Not found" />
      </div>
    );
  }
  if (!program || !childSlug) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <LoadingSkeleton lines={8} />
      </div>
    );
  }
  if (program.hubs.some((hub) => hub.slug === childSlug)) return <HubDetail />;
  const section = program.sections.find((item) => item.slug === childSlug);
  if (!section) return null;
  return <SectionPage program={program} section={section} />;
}
